import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Paper,
    Divider,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    CircularProgress,
    Alert,
    Card,
    CardContent,
    Chip,
} from '@mui/material';
import {
    Bookmark as BookmarkIcon,
    Route as RouteIcon,
    Timeline as TimelineIcon,
    TrendingUp as TrendingUpIcon,
} from '@mui/icons-material';
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    BarChart,
    Bar,
    Cell,
} from 'recharts';
import axios from 'axios';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d', '#ff7300'];

const SavedRidesPage: React.FC = () => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [stats, setStats] = useState<any>(null);
    const [recentSaves, setRecentSaves] = useState<any[]>([]);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [total, setTotal] = useState(0);

    const apiUrl = process.env.NODE_ENV === 'production'
        ? 'https://rideclubnet.com/api'
        : 'http://localhost:3001/api';

    useEffect(() => {
        fetchStats();
        fetchRecentSaves();
    }, [page, rowsPerPage]);

    const fetchStats = async () => {
        try {
            const token = localStorage.getItem('adminToken');
            const response = await axios.get(`${apiUrl}/admin/saved-rides/stats`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (response.data.success) {
                setStats(response.data.data);
            }
        } catch (err) {
            console.error('Fetch stats error:', err);
            setError('Failed to load saved rides statistics');
        }
    };

    const fetchRecentSaves = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('adminToken');
            const response = await axios.get(`${apiUrl}/admin/saved-rides/list`, {
                params: {
                    page: page + 1,
                    limit: rowsPerPage,
                },
                headers: { Authorization: `Bearer ${token}` }
            });
            if (response.data.success) {
                setRecentSaves(response.data.data);
                setTotal(response.data.pagination.total);
            }
        } catch (err) {
            console.error('Fetch list error:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleChangePage = (event: unknown, 控制新页: number) => {
        setPage(控制新页);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    if (loading && !stats) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h4" sx={{ mb: 4, fontWeight: 800 }}>
                Saved Rides Analytics 🔖
            </Typography>

            {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

            {/* Overview Cards */}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' }, gap: 3, mb: 4 }}>
                <Card sx={{ borderRadius: 4, boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
                    <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box sx={{ bgcolor: 'primary.light', p: 1.5, borderRadius: 3, color: 'primary.main' }}>
                            <BookmarkIcon fontSize="large" />
                        </Box>
                        <Box>
                            <Typography variant="h3" sx={{ fontWeight: 900 }}>{stats?.totalSaves || 0}</Typography>
                            <Typography variant="subtitle2" color="text.secondary">Total Rides Saved</Typography>
                        </Box>
                    </CardContent>
                </Card>

                <Card sx={{ borderRadius: 4, boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
                    <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box sx={{ bgcolor: 'success.light', p: 1.5, borderRadius: 3, color: 'success.main' }}>
                            <RouteIcon fontSize="large" />
                        </Box>
                        <Box>
                            <Typography variant="h3" sx={{ fontWeight: 900 }}>{stats?.popularRoutes?.length || 0}</Typography>
                            <Typography variant="subtitle2" color="text.secondary">Popular Routes Bookmarked</Typography>
                        </Box>
                    </CardContent>
                </Card>

                <Card sx={{ borderRadius: 4, boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
                    <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box sx={{ bgcolor: 'warning.light', p: 1.5, borderRadius: 3, color: 'warning.main' }}>
                            <TrendingUpIcon fontSize="large" />
                        </Box>
                        <Box>
                            <Typography variant="h3" sx={{ fontWeight: 900 }}>
                                {stats?.history?.slice(-1)[0]?.count || 0}
                            </Typography>
                            <Typography variant="subtitle2" color="text.secondary">Saves Today</Typography>
                        </Box>
                    </CardContent>
                </Card>
            </Box>

            {/* Charts Section */}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, gap: 3, mb: 4 }}>
                <Paper sx={{ p: 3, borderRadius: 4, boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
                    <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <TimelineIcon /> Save Activity (Last 30 Days)
                    </Typography>
                    <Box sx={{ height: 300 }}>
                        <ResponsiveContainer width="100%" height="100%">
                            <LineChart data={stats?.history || []}>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                <XAxis dataKey="date" />
                                <YAxis />
                                <Tooltip />
                                <Line type="monotone" dataKey="count" stroke="#8884d8" strokeWidth={3} dot={{ r: 4 }} />
                            </LineChart>
                        </ResponsiveContainer>
                    </Box>
                </Paper>

                <Paper sx={{ p: 3, borderRadius: 4, boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
                    <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <RouteIcon /> Top Routes
                    </Typography>
                    <Box sx={{ height: 300 }}>
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={stats?.popularRoutes || []} layout="vertical">
                                <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                                <XAxis type="number" />
                                <YAxis dataKey="route" type="category" width={150} />
                                <Tooltip />
                                <Bar dataKey="count">
                                    {stats?.popularRoutes?.map((entry: any, index: number) => (
                                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                    ))}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </Box>
                </Paper>
            </Box>

            {/* Top Saved Rides */}
            <Paper sx={{ p: 3, borderRadius: 4, boxShadow: '0 4px 20px rgba(0,0,0,0.05)', mb: 4 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>Most Saved Rides Currently Available</Typography>
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableRow>
                                    <TableCell>Ride ID</TableCell>
                                    <TableCell>Driver</TableCell>
                                    <TableCell>Origin</TableCell>
                                    <TableCell>Destination</TableCell>
                                    <TableCell>Date</TableCell>
                                    <TableCell align="right">Saves</TableCell>
                                </TableRow>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {stats?.topRides?.map((item: any) => (
                                <TableRow key={item.rideId}>
                                    <TableCell sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>{item.rideId.substring(0, 8)}...</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>{item.ride?.driver?.preferredName || item.ride?.driver?.firstName} {item.ride?.driver?.lastName ? item.ride?.driver?.lastName.charAt(0) + '.' : ''}</TableCell>
                                    <TableCell>{item.ride?.originCity?.name}</TableCell>
                                    <TableCell>{item.ride?.destinationCity?.name}</TableCell>
                                    <TableCell>{new Date(item.ride?.departureDateTime).toLocaleDateString()}</TableCell>
                                    <TableCell align="right">
                                        <Chip label={item._count.id} color="primary" sx={{ fontWeight: 800 }} />
                                    </TableCell>
                                </TableRow>
                            ))}
                            {(!stats?.topRides || stats?.topRides.length === 0) && (
                                <TableRow>
                                    <TableCell colSpan={6} align="center">No data available</TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>

            {/* Recent Activity Log */}
            <Paper sx={{ p: 3, borderRadius: 4, boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
                <Typography variant="h6" sx={{ mb: 2 }}>Recent Save Activity</Typography>
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>User</TableCell>
                                <TableCell>Ride</TableCell>
                                <TableCell>Saved At</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {recentSaves.map((save: any) => (
                                <TableRow key={save.id}>
                                    <TableCell>
                                        <Typography variant="body2" sx={{ fontWeight: 700 }}>{save.user.preferredName || save.user.firstName} {save.user.lastName ? save.user.lastName.charAt(0) + '.' : ''}</Typography>
                                        <Typography variant="caption" color="text.secondary">{save.user.email}</Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2">{save.ride.originCity.name} → {save.ride.destinationCity.name}</Typography>
                                        <Typography variant="caption" color="text.secondary">Driver: {save.ride.driver.preferredName || save.ride.driver.firstName}</Typography>
                                    </TableCell>
                                    <TableCell>{new Date(save.createdAt).toLocaleString()}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    rowsPerPageOptions={[5, 10, 25]}
                    component="div"
                    count={total}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />
            </Paper>
        </Box>
    );
};

export default SavedRidesPage;
